/* ==========================================================================================
   EcoVessel 2026 (forked from the Blenders Online build) · ev-product-details.js (productdetails builder)
   Behaviour of three product-page sections. Deferred, no dependencies, safe to load several times (the
   second copy does nothing), re-binds on shopify:section:load. Looks: ev-product-details.css.

   [data-ev-pd]         EV · Product details. Enhances the product's own description WITHOUT changing a word:
                        "On this page" from its headings (2+), the heading being read is marked, "Label: value"
                        specification lines side by side, bold-titled paragraph runs as a grid, wide tables wrapped.
   [data-ev-recs]       EV · Product recommendations. Fetches the complementary and related shelves (section
                        rendering), keeps "Recently viewed" in localStorage (handles only; this browser only).
   [data-ev-copy]       EV · Promo strip. Copies the code, says "Copied" (and tells a screen reader).
   ========================================================================================== */
(() => {
  'use strict';
  if (window.BOpd) return;
  const D = document;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const text = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim();
  const boInit = (el) => { if (window.BO && window.BO.init) window.BO.init(el); };

  /* ---------------------------------------------------------------- 1. Product details */
  const HEADS = 'h2, h3, h4, h5';
  const SPEC_HEAD = /specification|dimensions|technical|tech specs|details/i;
  const WARRANTY_HEAD = /warranty|guarantee/i;

  const slug = (s, used) => {
    let base = 'ev-pd-' + (s.toLowerCase().replace(/&amp;/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48) || 'part');
    let id = base, n = 2;
    while (used.has(id) || D.getElementById(id)) id = base + '-' + n++;
    used.add(id);
    return id;
  };

  // A line "Label: value" (the label short, the value present). Lines that hold a link are left alone.
  const splitLine = (s) => {
    const m = /^([^:]{2,40}):\s*(.+)$/.exec(s);
    return m ? [m[1].trim(), m[2].trim()] : null;
  };

  // Jugs and lids linked in the description ("Purchase Additional Jars Here: Ultra Jar ...") become real cards with
  // the photo, the price and a one-tap Add (Tommy 01-10). The section renders the cards in <template data-ev-addons>;
  // a list whose EVERY item links to one of them is swapped for the cards, in the list's order, and the words after a
  // link ("(the jar supplied with this model)") become the card's note. No template or no full match: unchanged.
  const addonLists = (root, desc) => {
    const tpl = root.querySelector('template[data-ev-addons]');
    if (!tpl) return;
    const cards = new Map();
    tpl.content.querySelectorAll('[data-ev-addon]').forEach((c) => cards.set(c.getAttribute('data-ev-addon'), c));
    if (!cards.size) return;
    const handleOf = (a) => { const m = /\/products\/([a-z0-9-]+)/i.exec(a.getAttribute('href') || ''); return m ? m[1].toLowerCase() : null; };
    desc.querySelectorAll('ul, ol').forEach((list) => {
      const items = [...list.children].filter((li) => li.tagName === 'LI');
      if (!items.length) return;
      const picked = items.map((li) => {
        const a = li.querySelector('a[href*="/products/"]');
        const h = a && handleOf(a);
        return h && cards.has(h) ? { li, a, h } : null;
      });
      if (picked.some((p) => !p)) return;
      const box = D.createElement('div');
      box.className = 'ev-addons';
      picked.forEach(({ li, a, h }) => {
        const card = cards.get(h).cloneNode(true);
        let note = text(li).replace(text(a), '').replace(/^[\s\-–:,(]+|[\s).]+$/g, '').trim();
        if (note) note = note.charAt(0).toUpperCase() + note.slice(1);
        const slot = card.querySelector('[data-ev-addon-note]');
        if (slot) { if (note) slot.textContent = note; else slot.remove(); }
        box.appendChild(card);
      });
      list.replaceWith(box);
      boInit(box);
    });
  };

  // <li>Weight: 7.2Kg</li> -> <li><span class="ev-pd__k">Weight:</span> <span class="ev-pd__v">7.2Kg</span></li>
  // Only text nodes are split, so every word (and any markup inside the value) stays exactly as written.
  const markPair = (li) => {
    // the label lives in the first text node, directly in the item or in its one wrapping <span>
    let host = li;
    if (li.children.length === 1 && li.firstElementChild.tagName === 'SPAN' && !text({ textContent: li.textContent.replace(li.firstElementChild.textContent, '') })) host = li.firstElementChild;
    let first = host.firstChild;
    while (first && first.nodeType === 3 && !first.nodeValue.trim()) first = first.nextSibling;
    if (!first || first.nodeType !== 3) return false;
    const i = first.nodeValue.indexOf(':');
    if (i < 2 || i > 40) return false;
    const k = D.createElement('span');
    k.className = 'ev-pd__k';
    k.textContent = first.nodeValue.slice(0, i + 1).trim();
    first.nodeValue = first.nodeValue.slice(i + 1).replace(/^\s+/, '');
    const v = D.createElement('span');
    v.className = 'ev-pd__v';
    while (host.firstChild) v.appendChild(host.firstChild);
    host.append(k, ' ', v);
    return true;
  };

  const specLists = (desc) => {
    // lists: most items "Label: value"
    desc.querySelectorAll('ul, ol').forEach((ul) => {
      if (ul.closest('.ev-pd__feats')) return;
      const items = [...ul.children].filter((c) => c.tagName === 'LI');
      if (items.length < 3 || ul.querySelector('a, img, iframe')) return;
      const pairs = items.filter((li) => splitLine(text(li)));
      if (pairs.length / items.length < 0.6) return;
      items.forEach((li) => { if (splitLine(text(li))) markPair(li); });
      ul.classList.add('ev-pd__specs');
    });
    // paragraphs of <br>-separated lines, most of them "Label: value": the same, as a list
    desc.querySelectorAll('p').forEach((p) => {
      if (p.querySelector('a, img, iframe, strong + br')) return;
      const html = p.innerHTML;
      const lines = html.split(/<br\s*\/?>/i).map((l) => l.trim()).filter((l) => l.replace(/<[^>]+>/g, '').trim());
      if (lines.length < 3) return;
      const plain = lines.map((l) => l.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim());
      const pairs = plain.filter(splitLine);
      if (pairs.length / lines.length < 0.6) return;
      const ul = D.createElement('ul');
      ul.className = 'ev-pd__specs';
      lines.forEach((l) => { const li = D.createElement('li'); li.innerHTML = l; ul.appendChild(li); if (splitLine(text(li))) markPair(li); });
      p.replaceWith(ul);
    });
  };

  // Runs of 2+ paragraphs that each open with a short bold title and a line break
  // (<p><strong>Program Settings</strong><br>Five program settings ...</p>) become a grid of feature notes.
  const featureRuns = (desc) => {
    const isFeat = (el) => {
      if (!el || el.tagName !== 'P') return false;
      const f = el.firstElementChild;
      if (!f || !/^(STRONG|B)$/.test(f.tagName)) return false;
      if (el.firstChild !== f && (el.firstChild.textContent || '').trim()) return false;
      const t = text(f);
      if (t.length < 3 || t.length > 60) return false;
      const next = f.nextElementSibling;
      return !!next && next.tagName === 'BR' && text(el).length > t.length + 8;
    };
    const kids = [...desc.children];
    for (let i = 0; i < kids.length; i++) {
      if (!isFeat(kids[i])) continue;
      const run = [kids[i]];
      while (isFeat(kids[i + run.length])) run.push(kids[i + run.length]);
      if (run.length >= 2) {
        const grid = D.createElement('div');
        grid.className = 'ev-pd__feats';
        run[0].before(grid);
        run.forEach((p) => {
          p.classList.add('ev-pd__feat');
          const b = p.firstElementChild;
          b.classList.add('ev-pd__feat-title');
          const br = b.nextElementSibling;
          if (br && br.tagName === 'BR') br.remove();
          grid.appendChild(p);
        });
      }
      i += run.length - 1;
    }
  };

  const INLINE = /^(A|STRONG|B|EM|I|SPAN|BR|SMALL|SUP|SUB|U|S|MARK|CODE)$/;
  const wrapOrphans = (desc) => {
    let run = [];
    const flushP = () => {
      if (run.some((n) => (n.textContent || '').trim() && n.nodeName !== 'BR')) {
        while (run.length && run[run.length - 1].nodeName === 'BR') run.pop().remove();
        const p = D.createElement('p');
        run[0].before(p);
        run.forEach((n) => p.appendChild(n));
      }
      run = [];
    };
    [...desc.childNodes].forEach((n) => {
      if (n.nodeType === 3 || (n.nodeType === 1 && INLINE.test(n.tagName))) run.push(n); else flushP();
    });
    flushP();
    let lis = [];
    const flushL = () => {
      if (lis.length) { const ul = D.createElement('ul'); lis[0].before(ul); lis.forEach((li) => ul.appendChild(li)); }
      lis = [];
    };
    [...desc.children].forEach((el) => { if (el.tagName === 'LI') lis.push(el); else flushL(); });
    flushL();
  };

  const bindDetails = (root) => {
    if (root.boPd) return;
    root.boPd = true;
    const desc = root.querySelector('[data-ev-pd-desc]');
    const nav = root.querySelector('[data-ev-pd-nav]');
    const grid = root.querySelector('.ev-pd__grid');
    if (!desc) return;

    // 0. loose markup: text straight in the description becomes a paragraph, and <li> items with no list
    //    around them (all 103 Robot Coupe pages) get their <ul>, so they read and space like the rest
    wrapOrphans(desc);
    // 1. empty headings the editor left behind (<h4><span></span></h4>, <h4><br></h4>)
    desc.querySelectorAll(HEADS + ', p').forEach((h) => { if (!text(h) && !h.querySelector('img, iframe, video, svg')) h.remove(); });
    // 2. YouTube: a light poster button (ev-main.js lite YouTube) until the visitor presses play;
    //    other videos: a name for the frame; tables: a box that scrolls on its own
    const play = root.querySelector('template[data-ev-pd-play]');
    if (window.BO && window.BO.init && play) {
      desc.querySelectorAll('iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]').forEach((f) => {
        const m = /embed\/([\w-]{6,20})/.exec(f.getAttribute('src') || '');
        if (!m) return;
        const label = desc.dataset.videoLabel || 'Product video';
        const box = D.createElement('div');
        box.className = 'ev-pd__yt';
        const b = D.createElement('button');
        b.type = 'button';
        b.className = 'ev-yt';
        b.setAttribute('data-ev-yt', m[1]);
        b.setAttribute('aria-label', 'Play video: ' + label);
        const img = D.createElement('img');
        // phone review 02-10: YouTube's 1280x720 cover (sharp, and 16:9 with no black bands to crop off). A video
        // without one answers with YouTube's 120x90 "no picture" image: then the 480x360 one, as before.
        const hq = 'https://i.ytimg.com/vi/' + m[1] + '/hqdefault.jpg';
        img.addEventListener('load', () => {
          if (img.naturalWidth <= 120) { if (img.src !== hq) img.src = hq; } else if (img.src !== hq) img.classList.add('is-169');
        });
        img.addEventListener('error', () => { if (img.src !== hq) img.src = hq; }, { once: true });
        img.src = 'https://i.ytimg.com/vi/' + m[1] + '/hq720.jpg';
        img.alt = ''; img.loading = 'lazy'; img.decoding = 'async'; img.width = 480; img.height = 360;
        const p = D.createElement('span'); p.className = 'ev-yt__play'; p.appendChild(play.content.cloneNode(true));
        const l = D.createElement('span'); l.className = 'ev-yt__label'; l.textContent = label;
        b.append(img, p, l);
        box.appendChild(b);
        const wrap = f.parentElement;
        if (wrap && wrap !== desc && !text(wrap) && wrap.querySelectorAll('iframe').length === 1) wrap.replaceWith(box); else f.replaceWith(box);
        window.BO.init(box);
      });
    }
    desc.querySelectorAll('iframe').forEach((f) => {
      if (!f.title) f.title = 'Product video';
      f.removeAttribute('width'); f.removeAttribute('height');
      const wrap = f.parentElement;
      if (wrap && wrap !== desc && wrap.children.length === 1 && !text(wrap)) wrap.classList.add('ev-pd__video');
      else { const w = D.createElement('div'); w.className = 'ev-pd__video'; f.before(w); w.appendChild(f); }
    });
    desc.querySelectorAll('table').forEach((t) => {
      if (t.parentElement.classList.contains('ev-pd__table')) return;
      const w = D.createElement('div'); w.className = 'ev-pd__table'; w.tabIndex = 0; w.setAttribute('role', 'region'); w.setAttribute('aria-label', 'Table');
      t.before(w); w.appendChild(t);
    });
    desc.querySelectorAll('a[href*=".pdf" i]').forEach((a) => a.classList.add('ev-pd__file'));
    featureRuns(desc);
    specLists(desc);
    addonLists(root, desc);

    // 3. the chapters: the highest heading level that is used twice or more
    const heads = [...desc.querySelectorAll(HEADS)].filter((h) => text(h).length > 1 && !h.closest('.ev-pd__feats'));
    let level = null;
    for (const tag of ['H2', 'H3', 'H4', 'H5']) {
      if (heads.filter((h) => h.tagName === tag).length >= 2) { level = tag; break; }
    }
    const chapters = level ? heads.filter((h) => h.tagName === level) : [];
    const used = new Set();
    chapters.forEach((h, i) => {
      h.classList.add('ev-pd__h');
      if (i === 0 && h === desc.firstElementChild) h.classList.add('is-first');
      if (!h.id) h.id = slug(text(h), used);
      h.tabIndex = -1;
      // spec-looking lists under a "Specifications" heading, even with fewer "Label:" lines, keep the plain list look
      if (SPEC_HEAD.test(text(h))) h.classList.add('ev-pd__h--spec');
    });

    // 4. the warranty link in the note beside it
    const wHead = heads.find((h) => WARRANTY_HEAD.test(text(h)));
    const wLink = root.querySelector('[data-ev-pd-warranty]');
    if (wHead && wLink) {
      if (!wHead.id) wHead.id = slug(text(wHead), used);
      wHead.tabIndex = -1;
      wLink.href = '#' + wHead.id;
      wLink.hidden = false;
    }

    // 5. "On this page"
    const max = parseInt(desc.dataset.maxNav, 10) || 10;
    if (nav && chapters.length >= 2) {
      const list = nav.querySelector('ol');
      chapters.slice(0, max).forEach((h) => {
        const li = D.createElement('li');
        const a = D.createElement('a');
        a.className = 'ev-pd__nav-link';
        a.href = '#' + h.id;
        // phone review 02-10: a heading that is a whole sentence made a 935px chip (TNC 5200): a long one is cut at a
        // word, and the whole heading stays its name and tooltip
        const full = text(h).replace(/:$/, '');
        const cut = full.length > 48 ? full.slice(0, 44).replace(/\s+\S*$/, '') + '…' : full;
        a.textContent = cut;
        if (cut !== full) { a.title = full; a.setAttribute('aria-label', full); }
        li.appendChild(a);
        list.appendChild(li);
      });
      nav.hidden = false;
      grid.classList.add('has-nav');
      const links = [...list.querySelectorAll('a')];

      const go = (h) => {
        h.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
        h.focus({ preventScroll: true });
        try { history.replaceState(null, '', '#' + h.id); } catch (e) { /* the editor's frame */ }
      };
      root.addEventListener('click', (e) => {
        const a = e.target.closest('a[href^="#ev-pd-"]');
        if (!a) return;
        const h = D.getElementById(a.getAttribute('href').slice(1));
        if (!h) return;
        e.preventDefault();
        go(h);
      });

      // the chapter being read: the last heading above the upper third of the screen
      const setOn = (id) => links.forEach((a) => {
        if (a.getAttribute('href') === '#' + id) { a.setAttribute('aria-current', 'location'); a.classList.add('is-on'); } else { a.removeAttribute('aria-current'); a.classList.remove('is-on'); }
      });
      let ticking = false;
      const spy = () => {
        ticking = false;
        const line = window.innerHeight * 0.3;
        let on = chapters[0];
        for (const h of chapters) { if (h.getBoundingClientRect().top - line <= 0) on = h; else break; }
        setOn(on.id);
        // phones: keep the chosen chip in view inside its sideways row
        const cur = list.querySelector('.is-on');
        if (cur && list.scrollWidth > list.clientWidth + 4) {
          const r = cur.getBoundingClientRect(), lr = list.getBoundingClientRect();
          if (r.left < lr.left || r.right > lr.right) list.scrollTo({ left: cur.offsetLeft - 16, behavior: reduce() ? 'auto' : 'smooth' });
        }
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(spy); } };
      const io = new IntersectionObserver((ens) => {
        ens.forEach((en) => {
          if (en.isIntersecting) window.addEventListener('scroll', onScroll, { passive: true }); else window.removeEventListener('scroll', onScroll);
          spy();   // once more on the way in and out, so the mark is right when the reader comes back
        });
      });
      io.observe(desc);
      root.boPdRelease = () => { io.disconnect(); window.removeEventListener('scroll', onScroll); };
      spy();

      // arriving with a chapter's hash
      if (location.hash && location.hash.startsWith('#ev-pd-')) {
        const h = D.getElementById(location.hash.slice(1));
        if (h && root.contains(h)) setTimeout(() => h.scrollIntoView({ block: 'start' }), 60);
      }
    }
    root.classList.add('is-ready');
  };

  /* ---------------------------------------------------------------- 2. Recommendations */
  const KEY = 'ev:recent';
  const store = {
    get() { try { const v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v.filter((h) => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(h)) : []; } catch (e) { return []; } },
    set(list) { try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 16))); } catch (e) { /* storage blocked: fine */ } },
    clear() { try { localStorage.removeItem(KEY); } catch (e) { /* fine */ } }
  };

  const html = (s) => new DOMParser().parseFromString(s, 'text/html');

  const fillShelf = (shelf, track) => {
    const slot = shelf.querySelector('[data-ev-recs-slot]');
    const rail = shelf.querySelector('[data-ev-rail-slot]');
    if (window.BO && window.BO.release && rail.hasAttribute('data-ev-rail')) window.BO.release(rail);
    slot.replaceWith(track);
    track.setAttribute('data-ev-recs-slot', '');
    rail.setAttribute('data-ev-rail', '');
    shelf.hidden = false;
    boInit(shelf);
  };
  const dropShelf = (shelf) => { if (shelf) shelf.remove(); };

  const loadIntent = (root, intent, url) => {
    const shelf = root.querySelector('[data-ev-recs-shelf="' + intent + '"]');
    if (!shelf || !url) { dropShelf(shelf); return; }
    if (intent === 'related') shelf.hidden = false;   // the skeleton shows while it loads (already shown by the markup)
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((t) => {
        const doc = html(t);
        const res = doc.querySelector('[data-ev-recs-result]');
        const track = res && res.querySelector('.ev-rail__track');
        if (!track || !track.children.length) return dropShelf(shelf);
        const own = root.dataset.product;
        [...track.children].forEach((li) => { const a = li.querySelector('a[href*="/products/' + own + '"]'); if (a && own) li.remove(); });
        if (!track.children.length) return dropShelf(shelf);
        fillShelf(shelf, track);
      })
      .catch(() => dropShelf(shelf));
  };

  const loadRecent = (root) => {
    const shelf = root.querySelector('[data-ev-recs-shelf="recent"]');
    const own = root.dataset.product;
    const list = store.get();
    // remember this product, newest first
    if (own) store.set([own].concat(list.filter((h) => h !== own)));
    if (!shelf) return;
    const limit = parseInt(root.dataset.recent, 10) || 6;
    const want = list.filter((h) => h !== own).slice(0, limit);
    if (!want.length) return dropShelf(shelf);
    const base = (root.dataset.root || '/').replace(/\/?$/, '/');
    Promise.all(want.map((h) => fetch(base + 'products/' + encodeURIComponent(h) + '?section_id=ev-product-recs', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => { const c = t && html(t).querySelector('[data-ev-recs-card]'); return c ? c.firstElementChild : null; })
      .catch(() => null)))
      .then((cards) => {
        const gone = want.filter((h, i) => !cards[i]);
        if (gone.length) store.set(store.get().filter((h) => !gone.includes(h)));   // a product that no longer exists
        const ul = D.createElement('ul');
        ul.className = 'ev-rail__track';
        ul.setAttribute('role', 'list');
        cards.filter(Boolean).forEach((c) => { const li = D.createElement('li'); li.appendChild(c); ul.appendChild(li); });
        if (!ul.children.length) return dropShelf(shelf);
        fillShelf(shelf, ul);
        const clear = shelf.querySelector('[data-ev-recs-clear]');
        if (clear) clear.addEventListener('click', () => {
          store.clear();
          if (own) store.set([own]);
          const h2 = shelf.querySelector('h2');
          shelf.remove();
          if (window.BO && window.BO.toast) window.BO.toast('Recently viewed cleared');
          else if (h2) h2.blur();
        });
      });
  };

  const bindRecs = (root) => {
    if (root.boRecs) return;
    root.boRecs = true;
    // "Recently viewed" takes its room now, while it is far below the screen, when this browser has other
    // products to show; nothing then pushes the page down later (critics n23).
    if (root.dataset.recent) {
      const shelf = root.querySelector('[data-ev-recs-shelf="recent"]');
      const own = root.dataset.product;
      if (shelf && store.get().some((h) => h !== own)) shelf.hidden = false;
    }
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      loadIntent(root, 'complementary', root.dataset.compUrl);
      loadIntent(root, 'related', root.dataset.relatedUrl);
      if (root.dataset.recent) loadRecent(root);
      else if (root.dataset.product) { const l = store.get(), own = root.dataset.product; store.set([own].concat(l.filter((h) => h !== own))); }
    };
    // Ask once the page has loaded (idle), or sooner if the shopper is already near the shelves: the cards are
    // in long before anyone scrolls down to them, so they never land under the reader's thumb.
    const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 400));
    if (D.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((ens) => { if (ens.some((e) => e.isIntersecting)) { io.disconnect(); start(); } }, { rootMargin: '1200px 0px' });
      io.observe(root);
    } else start();
  };

  /* ---------------------------------------------------------------- 3. Copy a code */
  const copy = (s) => {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(s);
    return new Promise((ok, no) => {
      const t = D.createElement('textarea');
      t.value = s; t.setAttribute('readonly', ''); t.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
      D.body.appendChild(t); t.select();
      let done = false;
      try { done = D.execCommand('copy'); } catch (e) { done = false; }
      t.remove();
      if (done) ok(); else no();
    });
  };
  D.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ev-copy]');
    if (!btn) return;
    const code = btn.dataset.boCopy;
    const status = btn.parentElement.querySelector('[data-ev-copy-status]');
    clearTimeout(btn.boT);
    copy(code).then(() => {
      btn.classList.add('is-copied');
      if (status) status.textContent = (btn.dataset.copied || 'Copied') + ': ' + code;
      btn.boT = setTimeout(() => { btn.classList.remove('is-copied'); if (status) status.textContent = ''; }, 2600);
    }, () => {
      // the browser refused: show the code selected so a long-press or Ctrl+C takes it
      const b = btn.querySelector('.ev-promo__code');
      if (b) { const r = D.createRange(); r.selectNodeContents(b); const sel = getSelection(); sel.removeAllRanges(); sel.addRange(r); }
      if (status) status.textContent = btn.dataset.failed || '';
      if (window.BO && window.BO.toast && btn.dataset.failed) window.BO.toast(btn.dataset.failed + ': ' + code, { type: 'error' });
    });
  });

  /* ---------------------------------------------------------------- binding */
  const init = (scope) => {
    const r = scope || D;
    r.querySelectorAll('[data-ev-pd]').forEach(bindDetails);
    r.querySelectorAll('[data-ev-recs]').forEach(bindRecs);
  };
  window.BOpd = { init };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init()); else init();
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => { e.target.querySelectorAll('[data-ev-pd]').forEach((el) => { if (el.boPdRelease) el.boPdRelease(); }); });
})();
